import BaseLayer from 'ol/layer/Base';
import {AfterViewInit, Component, effect, ElementRef, inject, signal, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatOption} from '@angular/material/autocomplete';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatSelect} from '@angular/material/select';
import {scaleThreshold} from 'd3-scale';
import GeoJSON from 'ol/format/GeoJSON';
import {Feature} from 'ol';
import {Geometry} from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import {map, startWith} from 'rxjs';
import {GenIceCountySummary} from '../../../../generated/serverModels/GenIceCountySummary';
import {GenIceSector} from '../../../../generated/serverModels/GenIceSector';
import {AppService} from '../../../../services/app.service';
import {IceService} from '../../../../services/ice.service';
import {MapService} from '../../../../services/map.service';
import {BaseUILayer} from '../../../classes/layers/base-ui-layer';
import {CompactCurrencyPipe} from '../../../pipes/compact-currency.pipe';
import {LayerHeaderComponent} from '../../layer-header/layer-header.component';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';

type IceLayerFeature = {
  properties: GenIceCountySummary & {
    county: string;
    tastiDetails?: GenIceCountySummary;
    ntlDetails?: GenIceCountySummary;
  };
};

@Component({
  standalone: true,
  selector: 'tgrid-ice-layer',
  imports: [
    LayerHeaderComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    CompactCurrencyPipe,
  ],
  templateUrl: './ice-layer.component.html',
  styleUrl: './ice-layer.component.scss',
})
export class IceLayerComponent extends BaseUILayer<BaseLayer> implements AfterViewInit {

  private iceService = inject(IceService);
  private appService = inject(AppService);
  //to change the legend, just change this list to what colors you want. Domain is automatically bucketed to colors.
  private colors = ['#580000', '#a54d15', '#edc58d', '#ffffe0', '#cce2cf', '#48aaa7', '#02881d'].reverse();
  protected legendBounds = signal({min: Number.MAX_SAFE_INTEGER, max: Number.MIN_SAFE_INTEGER});

  protected sectors = GenIceSector;
  private hatchPattern = (() => {
    const canvas = document.createElement('canvas');
    canvas.width = 12;
    canvas.height = 12;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return undefined;
    }

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 12);
    ctx.lineTo(12, 0);
    ctx.stroke();
    return ctx.createPattern(canvas, 'repeat') ?? undefined;
  })();


  protected filterGroup: FormGroup = new FormGroup({
    sector: new FormControl<GenIceSector>(GenIceSector.ALL, {nonNullable: true}),
  });

  @ViewChild('legendBar') legendBarElement!: ElementRef<HTMLDivElement>;

  constructor() {
    super();
    const filterSignal = toSignal(this.filterGroup.valueChanges.pipe(startWith(this.filterGroup.value)));
    effect(() => {
      this.appService.selectedStates();
      this.appService.selectedYears();
      filterSignal();
      if (this.checked) {
        MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
        this.getCountyCostData();
      }
    });


    this.iceService.countyCostData$.pipe(
      map((res) => {
        const collection = {
          type: 'FeatureCollection',
          features: res.features.map((f) => {
            (f.properties as unknown as Record<string, unknown>)['popoverData'] = IceService.getPopoverForFeature(f);
            return f;
          }),
        };
        this.legendBounds.set({
          min: res.scale.min,
          max: res.scale.max,
        });

        return [collection, res.scale.toValue] satisfies [{
          type: string,
          features: IceLayerFeature[]
        }, (value: number) => number];
      }),
      takeUntilDestroyed(),
    )
      .subscribe({
          next: ([featureCollection, normalize]: [{
            type: string,
            features: IceLayerFeature[]
          }, (value: number) => number]) => {
            // `toValue` returns normalized values centered around quartiles (q1=0, q3=1),
            // so thresholds must also be in normalized space.
            const domain = [];
            for (let i = 1; i < this.colors.length; i++) {
              domain.push(i / this.colors.length);
            }
            const colorScale = scaleThreshold<number, string>(
              domain,
              this.colors,
            );

            const features = new GeoJSON().readFeatures(featureCollection);
            this.mapLayer().renderedLayer = new VectorLayer({
              source: new VectorSource({
                features: features as Feature<Geometry>[],
                attributions: this.mapLayer().buildAttribution(),
              }),
              properties: {
                name: this.mapLayer().displayName,
              },
              opacity: 0.8,
              style: (feature, resolution: number) => {
                const properties = feature.getProperties() as IceLayerFeature['properties'];
                const legendValue = properties.tastiDetails?.totalCost;
                const hasNtl = properties.ntlDetails?.source?.name === 'NTL';

                const baseStyle = new Style({
                  stroke: new Stroke({
                    color: 'black',
                    width: 1,
                  }),
                  fill: new Fill({
                    color: legendValue !== undefined ? colorScale(normalize(legendValue)) : '#444',
                  }),
                  text: legendValue !== undefined ? new Text({
                    text: (resolution > 3000) ? '' : '$' + Intl.NumberFormat('en', {notation: 'compact'}).format(legendValue),
                  }) : undefined,
                });

                if (hasNtl && this.hatchPattern) {
                  return [
                    baseStyle,
                    new Style({
                      fill: new Fill({
                        color: this.hatchPattern,
                      }),
                    }),
                  ];
                }

                return baseStyle;

              },
            });

            MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
          },
        },
      );


  }

  ngAfterViewInit(): void {
    this.legendBarElement.nativeElement.style.background = `linear-gradient(to right, ${this.colors.join(',')})`;
  }

  override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (this.checked) {
      this.getCountyCostData();
    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override onDestroy(): void {
  }

  private getCountyCostData(): void {
    const selectedYears = this.appService.selectedYears();
    const states = this.appService.selectedStates();

    if (selectedYears.length === 0) return;

    const sector: GenIceSector = this.filterGroup.get('sector')?.value;

    this.iceService.getCountyCostData(selectedYears, states, sector);


  }

  getSectorDisplayName(sector: GenIceSector): string {
    return sector.name.replace(/_/g, '-').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }
}
